import { config } from '../lib/config'
import type {
  ApiErrorCode,
  HealthResponse,
  QuoteRequestBody,
  QuoteResponse,
  SwapRequestBody,
  SwapResponse,
  TokenListResponse,
} from './types'

/**
 * The only way this app reaches the routing engine.
 *
 * Two things it deliberately does not do. It never re-quotes on behalf of a caller — an
 * expired or unknown route id comes back as `ROUTE_EXPIRED` / `ROUTE_NOT_FOUND` and stays
 * that way, because a silent re-quote hands the user calldata for a price they never saw at
 * the moment they are about to sign it. And it never computes an amount: `/quote` and
 * `/swap` are the only sources of every number the UI shows.
 */

export class ApiClientError extends Error {
  readonly code: ApiErrorCode | 'NETWORK'
  readonly status: number
  readonly detail: Record<string, unknown>

  constructor(code: ApiErrorCode | 'NETWORK', message: string, status: number, detail: Record<string, unknown> = {}) {
    super(message)
    this.name = 'ApiClientError'
    this.code = code
    this.status = status
    this.detail = detail
  }

  /** Worth showing a retry affordance for: the pair may be fine and the cause operational. */
  get isTransient(): boolean {
    return (
      this.code === 'NETWORK' ||
      this.code === 'NODE_UNAVAILABLE' ||
      this.code === 'POOL_STATE_STALE' ||
      this.code === 'RATE_LIMITED' ||
      this.code === 'INTERNAL'
    )
  }
}

/**
 * The rate-limit identity.
 *
 * The API keys its per-client bucket on `x-client-id` and falls back to the socket address,
 * which behind NAT is shared by everyone in the office — one person typing an amount then
 * exhausts the 5 rps bucket for the rest. A per-tab id makes the bucket per-tab. It is not
 * an authentication token and the API does not treat it as one.
 */
const CLIENT_ID_KEY = 'sparkdex.clientId'

function clientId(): string {
  try {
    const existing = sessionStorage.getItem(CLIENT_ID_KEY)
    if (existing) return existing
    const minted = mintClientId()
    sessionStorage.setItem(CLIENT_ID_KEY, minted)
    return minted
  } catch {
    // Private mode, or storage disabled. A per-load id is still better than none.
    return mintClientId()
  }
}

/**
 * `crypto.randomUUID` exists only in a secure context — HTTPS, or localhost. On a plain-HTTP
 * page at any other address it is undefined, and calling it used to throw inside `request`'s
 * fetch `try`, which reported every call as NETWORK without one leaving the browser.
 * `getRandomValues` has no such restriction. The id names a rate-limit bucket and is not a
 * secret, so its format does not matter.
 */
function mintClientId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

async function request<T>(path: string, init: RequestInit & { signal?: AbortSignal }): Promise<T> {
  // Outside the `try`: only a failed fetch is NETWORK. Anything else thrown here is a bug in
  // this app and must surface as itself, not as an unreachable API.
  const headers = { 'content-type': 'application/json', 'x-client-id': clientId(), ...init.headers }
  let response: Response
  try {
    response = await fetch(`${config.apiBaseUrl}${path}`, { ...init, headers })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiClientError('NETWORK', `could not reach the quote API at ${config.apiBaseUrl}`, 0, {
      cause: String(error),
    })
  }

  const text = await response.text()
  let body: unknown = null
  if (text.length > 0) {
    try {
      body = JSON.parse(text)
    } catch {
      throw new ApiClientError('INTERNAL', `the API returned a non-JSON response (${response.status})`, response.status)
    }
  }

  if (!response.ok) {
    const error = (body as { error?: Record<string, unknown> } | null)?.error
    const code = (error?.code as ApiErrorCode | undefined) ?? 'INTERNAL'
    const message = (error?.message as string | undefined) ?? `request failed with ${response.status}`
    const { code: _code, message: _message, ...detail } = error ?? {}
    throw new ApiClientError(code, message, response.status, detail)
  }

  return body as T
}

export function postQuote(body: QuoteRequestBody, signal?: AbortSignal): Promise<QuoteResponse> {
  return request<QuoteResponse>('/quote', {
    method: 'POST',
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  })
}

export function postSwap(body: SwapRequestBody, signal?: AbortSignal): Promise<SwapResponse> {
  return request<SwapResponse>('/swap', {
    method: 'POST',
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  })
}

export function getTokens(signal?: AbortSignal): Promise<TokenListResponse> {
  return request<TokenListResponse>('/tokens', { method: 'GET', ...(signal ? { signal } : {}) })
}

/**
 * `/health` answers 503 when unhealthy, with the full report in the body rather than an
 * `{ error }` envelope — a load balancer is meant to read the status and nothing else. So
 * this goes around `request`, which would classify the 503 as a failure and throw away the
 * one thing worth having. An unhealthy API is not an error to this app; it is exactly what
 * the status strip exists to show.
 */
export async function getHealth(signal?: AbortSignal): Promise<HealthResponse> {
  const headers = { 'x-client-id': clientId() }
  let response: Response
  try {
    response = await fetch(`${config.apiBaseUrl}/health`, {
      headers,
      ...(signal ? { signal } : {}),
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiClientError('NETWORK', `could not reach the quote API at ${config.apiBaseUrl}`, 0, {
      cause: String(error),
    })
  }

  const body = (await response.json().catch(() => null)) as HealthResponse | null
  if (!body || typeof body.status !== 'string') {
    throw new ApiClientError('INTERNAL', `/health returned an unreadable body (${response.status})`, response.status)
  }
  return body
}
